import { useCallback } from 'react'
import { graphql, useFragment, useMutation } from 'react-relay'

import { type useSetUserEmailNotificationsEnabledMutation_user$key } from '@/__generated__/useSetUserEmailNotificationsEnabledMutation_user.graphql'
import {
  type useSetUserEmailNotificationsEnabledMutation as UseSetUserEmailNotificationsEnabledMutation,
  type useSetUserEmailNotificationsEnabledMutation$data
} from '@/__generated__/useSetUserEmailNotificationsEnabledMutation.graphql'

type CommitAsyncFn = (enabled: boolean) => Promise<useSetUserEmailNotificationsEnabledMutation$data>

export const useSetUserEmailNotificationsEnabledMutation = (
  userRef: useSetUserEmailNotificationsEnabledMutation_user$key | null | undefined
): [CommitAsyncFn, boolean] => {
  const userData = useFragment(
    graphql`
      fragment useSetUserEmailNotificationsEnabledMutation_user on User {
        rowId
      }
    `,
    userRef
  )
  const [commit, isPending] = useMutation<UseSetUserEmailNotificationsEnabledMutation>(graphql`
    mutation useSetUserEmailNotificationsEnabledMutation($input: UpdateUserInput!) {
      updateUser(input: $input) {
        user {
          isEmailNotificationsEnabled
          updatedAt
        }
      }
    }
  `)

  const commitAsync = useCallback<CommitAsyncFn>(
    (enabled: boolean) =>
      new Promise<useSetUserEmailNotificationsEnabledMutation$data>((resolve, reject) =>
        commit({
          onCompleted: (response, errors) => {
            if (errors) {
              reject(errors)
            } else {
              resolve(response)
            }
          },
          // transport-level failures (offline, 5xx, WS drop) invoke onError, not onCompleted — without
          // this the promise would never settle and the caller would hang forever.
          onError: (error) => reject(error),
          variables: {
            input: {
              rowId: userData?.rowId,
              patch: {
                isEmailNotificationsEnabled: enabled
              }
            }
          }
        })
      ),
    [commit, userData?.rowId]
  )

  return [commitAsync, isPending]
}
